/**
 * FIFO queue of dApp requests awaiting the user's decision (ENTX-A-4).
 *
 * The modal always shows the HEAD of the queue. A new request is appended —
 * it never replaces the one the user is reading, so a dApp can't swap the
 * request just before the click, and every request gets an answer.
 */

export interface QueuedWcRequest {
  request: { id: number; topic: string };
}

/** Upper bound per queue; further requests are refused rather than buffered. */
export const MAX_QUEUED_REQUESTS = 10;

export type EnqueueResult<T> =
  | { queue: T[]; accepted: true }
  | { queue: T[]; accepted: false; reason: 'full' | 'duplicate' };

/**
 * Append `item`. A re-delivered request (same topic + id — WalletKit replays
 * pending requests after a reload) is a no-op; a full queue refuses the item
 * so the caller can answer the dApp with an error.
 */
export function enqueueRequest<T extends QueuedWcRequest>(
  queue: readonly T[],
  item: T,
  max: number = MAX_QUEUED_REQUESTS
): EnqueueResult<T> {
  const duplicate = queue.some(
    (q) => q.request.topic === item.request.topic && q.request.id === item.request.id
  );
  if (duplicate) return { queue: [...queue], accepted: false, reason: 'duplicate' };
  if (queue.length >= max) return { queue: [...queue], accepted: false, reason: 'full' };
  return { queue: [...queue, item], accepted: true };
}

/** Remove one request (after it was answered). */
export function removeRequest<T extends QueuedWcRequest>(
  queue: readonly T[],
  topic: string,
  id: number
): T[] {
  return queue.filter((q) => !(q.request.topic === topic && q.request.id === id));
}

/** Remove every request of a session (it was deleted). */
export function removeTopic<T extends QueuedWcRequest>(queue: readonly T[], topic: string): T[] {
  return queue.filter((q) => q.request.topic !== topic);
}

/** Remove an expired request wherever it sits. */
export function removeRequestId<T extends QueuedWcRequest>(queue: readonly T[], id: number): T[] {
  return queue.filter((q) => q.request.id !== id);
}
